import { ChangeDetectionStrategy, Component, effect, ElementRef, EventEmitter, HostBinding, Input, Output, signal } from '@angular/core';
import { IAppConfig } from 'app/app-config';
import { mergeMap } from 'rxjs';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { filter, map } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { User } from 'app/account/model/user';
import { AsyncPipe } from '@angular/common';
import { SvgIconDirective } from '@styleguide/svg-icon/svg-icon.directive';
import { ProfileImageComponent } from '@styleguide/profile/profile-image/profile-image.component';
import { ApiImageUrlPipe } from '@customer-apps/api-image/api-image-url.pipe';
import { PageTitleService } from '@customer-apps/page-title/page-title.service';
import { BackLinkService } from '@customer-apps/router/back-link.service';
import { ConfigService } from '@customer-apps/config/config.service';
import { OrganizationProfile } from 'app/organization/model/organization-profile';

export type headerComponentState = 'idle' | 'mainMenuOpen' | 'userMenuOpen';

@Component({
  selector: 'kgr-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [SvgIconDirective, ProfileImageComponent, AsyncPipe, ApiImageUrlPipe],
})
export class HeaderComponent {
  @Output() stateChange = new EventEmitter<headerComponentState>();

  @HostBinding('class.kgr-pageHeader')
  @HostBinding('class.kgr-pageHeader--fullWidth')
  defaultClass = true;

  @Input() organization?: OrganizationProfile;
  @Input() user?: User;

  state = signal<headerComponentState>('idle');

  displayBackLink$ = this.backLinkService.displayBackLink$;

  constructor(
    private titleService: PageTitleService,
    private backLinkService: BackLinkService,
    private configService: ConfigService<IAppConfig>,
    private elementRef: ElementRef,
    private router: Router,
    private activatedRoute: ActivatedRoute,
  ) {
    effect(() => {
      this.stateChange.emit(this.state());
    });

    this.router.events
      .pipe(
        takeUntilDestroyed(),
        filter((e) => e instanceof NavigationEnd),
        map(() => this.activatedRoute),
        map((route) => {
          while (route.firstChild) {
            route = route.firstChild;
          }

          return route;
        }),
        mergeMap((route) => route.data),
      )
      .subscribe((data) => {
        // TODO: inverted header still supported?
        if (data.invertedHeader) {
          this.elementRef.nativeElement.classList.add('is-inverted');
        } else {
          this.elementRef.nativeElement.classList.remove('is-inverted');
        }

        this.closeMenus();
      });
  }

  onMainMenuToggleClick(event: Event) {
    event.preventDefault();
    event.stopPropagation();
    if (this.state() === 'mainMenuOpen') {
      this.state.set('idle');
    } else {
      this.state.set('mainMenuOpen');
    }
  }

  onUserMenuToggleClick(event: Event) {
    event.preventDefault();
    event.stopPropagation();
    if (this.state() === 'userMenuOpen') {
      this.state.set('idle');
    } else {
      this.state.set('userMenuOpen');
    }
  }

  onBackLinkClick() {
    if (this.displayBackLink$.value) {
      this.backLinkService.navigateBack();
    }
  }

  closeMenus() {
    this.state.set('idle');
  }
}
